import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { CredentialFieldsDisplay } from "components/PortalCredential/CredentialFieldsDisplay";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { FieldDisplay } from "../Fulfillment/FulfillmentDetails/shared/FieldComponents";
import { useCohortDetails } from "./useCohortDetails";

export function CohortCredentials() {
  const theme = useTheme();
  const { data } = useCohortDetails();
  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "cohort-credentials-expanded",
    { defaultValue: true }
  );

  const cohort = data?.cohort;
  const cohortState = cohort?.state;
  const portalCredential = cohort?.portalCredential;
  const credential = portalCredential?.credential;

  // Don't render if no credentials available
  if (!portalCredential || !credential) {
    return (
      <Stack
        direction="column"
        component={Paper}
        elevation={0}
        spacing={2}
        padding={theme.spacing(2, 3)}
      >
        <Typography variant="h6">State Portal</Typography>
        <Typography variant="body2" color="text.secondary">
          No credential associated with cohort and no credentials found among
          members. Ensure at least one cohort member has a TIERII credential
          assigned to their facility.
        </Typography>
      </Stack>
    );
  }

  const credentialsContent = (
    <CredentialFieldsDisplay
      credential={credential}
      portalUrl={portalCredential.url}
      showCombinedInvoicing={cohortState === "WV"}
    />
  );

  const verificationStatusOnly = credential && (
    <FieldDisplay
      label="Verification Status:"
      value={prettyPrintEnumValue(credential.verificationStatus)}
      noWrap
    />
  );

  return (
    <Stack
      direction="column"
      component={Paper}
      elevation={0}
      spacing={2}
      padding={theme.spacing(2, 3)}
    >
      {/* Header with title and expand/collapse button */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Typography variant="h6" data-testid="cohort-credentials-title">
            State Portal
          </Typography>
          <IconButton size="small">
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Stack>

      {/* Show only verification status when collapsed */}
      {!isExpanded && verificationStatusOnly && (
        <Box>{verificationStatusOnly}</Box>
      )}

      <Collapse in={isExpanded}>
        <Stack spacing={2}>
          {/* Credentials content */}
          {credentialsContent}
        </Stack>
      </Collapse>
    </Stack>
  );
}
